<!-- src/components/shared/LazyLottie.vue -->
<template>
	<div ref="lottieContainer" class="lottie-animation-container" :style="containerStyle" v-if="isLottieLoaded" />
	<div v-else-if="isLoading" class="lottie-loading">
		<slot name="loading">
			<!-- Default loading state -->
			<div class="w-[100px] h-[100px] flex items-center justify-center text-4xl">📍</div>
		</slot>
	</div>
	<div v-else-if="hasError" class="lottie-error">
		<slot name="error">
			<div class="w-[100px] h-[100px] flex items-center justify-center text-2xl">❌</div>
		</slot>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'

// Props - same as vue3-lottie
const props = defineProps({
	animationData: {
		type: Object,
		required: true,
	},
	loop: {
		type: [Boolean, Number],
		default: true,
	},
	autoplay: {
		type: Boolean,
		default: true,
	},
	width: {
		type: [Number, String],
		default: '100%',
	},
	height: {
		type: [Number, String],
		default: '100%',
	},
	speed: {
		type: Number,
		default: 1,
	},
	renderer: {
		type: String,
		default: 'svg',
	},
	backgroundColor: {
		type: String,
		default: 'transparent',
	},
})

// Emits
const emit = defineEmits(['onComplete', 'onLoopComplete', 'onEnterFrame', 'onAnimationLoaded'])

// Reactive state
const lottieContainer = ref(null)
const isLoading = ref(true)
const hasError = ref(false)
const isLottieLoaded = ref(false)
let animationInstance = null

// CDN URL for lottie-web only
const LOTTIE_CDN_URL = '/js/lottie.min.js'

// Computed styles
const containerStyle = computed(() => {
	let width = props.width
	let height = props.height

	if (typeof props.width === 'number') {
		width = `${props.width}px`
	}
	if (typeof props.height === 'number') {
		height = `${props.height}px`
	}

	return {
		width,
		height,
		backgroundColor: props.backgroundColor,
		overflow: 'hidden',
	}
})

// Load lottie-web from CDN
const loadLottieScript = () => {
	return new Promise((resolve, reject) => {
		// Check if lottie is already loaded
		if (window.lottie) {
			resolve()
			return
		}

		// Check if script is already in DOM
		const existingScript = document.querySelector(`script[src="${LOTTIE_CDN_URL}"]`)
		if (existingScript) {
			// Wait for it to load
			if (window.lottie) {
				resolve()
			} else {
				existingScript.onload = resolve
				existingScript.onerror = reject
			}
			return
		}

		const script = document.createElement('script')
		script.src = LOTTIE_CDN_URL
		script.onload = () => {
			console.log('✅ Lottie-web loaded successfully')
			resolve()
		}
		script.onerror = () => reject(new Error(`Failed to load lottie-web from ${LOTTIE_CDN_URL}`))
		document.head.appendChild(script)
	})
}

// Initialize animation
const initializeAnimation = () => {
	if (!lottieContainer.value || !window.lottie || !props.animationData) {
		return
	}

	// Destroy existing animation if any
	if (animationInstance) {
		animationInstance.destroy()
		animationInstance = null
	}

	try {
		// Create animation
		animationInstance = window.lottie.loadAnimation({
			container: lottieContainer.value,
			renderer: props.renderer,
			loop: props.loop,
			autoplay: props.autoplay,
			animationData: props.animationData,
		})

		// Set speed
		if (props.speed !== 1) {
			animationInstance.setSpeed(props.speed)
		}

		// Add event listeners
		animationInstance.addEventListener('complete', () => {
			emit('onComplete')
		})

		animationInstance.addEventListener('loopComplete', () => {
			emit('onLoopComplete')
		})

		animationInstance.addEventListener('enterFrame', () => {
			emit('onEnterFrame')
		})

		animationInstance.addEventListener('DOMLoaded', () => {
			emit('onAnimationLoaded')
		})

		console.log('✅ Lottie animation initialized')
	} catch (error) {
		console.error('❌ Failed to initialize Lottie animation:', error)
		hasError.value = true
	}
}

// Load lottie and initialize
const setup = async () => {
	try {
		isLoading.value = true
		hasError.value = false

		// Load lottie-web from CDN
		await loadLottieScript()

		// Mark as loaded
		isLottieLoaded.value = true
		isLoading.value = false

		// Initialize animation in next tick
		await new Promise((resolve) => setTimeout(resolve, 0))
		initializeAnimation()
	} catch (error) {
		console.error('❌ Failed to load Lottie:', error)
		hasError.value = true
		isLoading.value = false
	}
}

// Watch for animation data changes
watch(
	() => props.animationData,
	() => {
		if (isLottieLoaded.value) {
			initializeAnimation()
		}
	},
	{ deep: true },
)

// Lifecycle
onMounted(() => {
	setup()
})

onBeforeUnmount(() => {
	if (animationInstance) {
		animationInstance.destroy()
		animationInstance = null
	}
})

// Expose methods (optional - for advanced use)
defineExpose({
	play: () => animationInstance?.play(),
	pause: () => animationInstance?.pause(),
	stop: () => animationInstance?.stop(),
	setSpeed: (speed) => animationInstance?.setSpeed(speed),
})
</script>

<style scoped>
.lottie-animation-container {
	display: flex;
	align-items: center;
	justify-content: center;
}

.lottie-loading,
.lottie-error {
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>
